/**
 * MapsPage — rebuilt with Mapbox GL.
 *
 * Uses the shared Mapbox engine (MapWorkspace/MapCanvas) instead of
 * Leaflet. Preserves all existing functionality: field creation/editing,
 * search, selection drawers, drawing overlay, and layer management.
 */

import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import {
  MapIcon,
  PlusIcon,
  UserGroupIcon,
  BuildingStorefrontIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  MapPinIcon,
  CheckCircleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

import { apiGet, apiPatch, apiPost, apiDelete } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { RWANDA_DEFAULTS } from '../utils/geospatial';
import { buildCropOptions, normalizeCropName } from '../utils/cropCatalog';

// New Mapbox engine
import { MapWorkspace, MapEmptyState, MapErrorState } from '../features/maps';
import { useMapViewport } from '../features/maps/hooks/useMapViewport';
import { useMapLayers } from '../features/maps/hooks/useMapLayers';
import { useMapSelection } from '../features/maps/hooks/useMapSelection';
import { calculateAreaHectares, toBoundary } from '../features/maps/utils/coordinates';
import type { MapField, MapTree, MapFarmer, MapCohort, MapWarehouse, GeoCoord, LayerVisibility } from '../features/maps/types/mapTypes';
import type { BoundaryLevel } from '../features/maps/layers/adminBoundaryLayer';

// ─── Sub-component types (preserved from old MapsPage) ──────────────

interface FieldFarmer {
  id: number;
  name?: string;
  phone?: string;
  farmer_code?: string;
  role_on_field?: string;
  is_primary?: boolean;
  isPrimary?: boolean;
}

interface DiaryEntrySummary {
  id: number;
  visit_date?: string;
  visit_type?: string;
  crop_type?: string;
  health_status?: string;
  severity_level?: string;
  observations?: string;
  scout_name?: string;
}

interface FieldHistoryEntry {
  id: number;
  report_code: string;
  assignment_id: number;
  assignment_code: string;
  activity_type_name?: string;
  status: string;
  farmer_name?: string;
  submitted_at?: string;
  area_completed?: number;
  quantity_used?: number;
  observation_count?: number;
}

interface FarmerOption {
  id: number;
  full_name?: string;
  name?: string;
  phone?: string;
  farmer_code?: string;
}

interface StaffOption {
  id: number;
  full_name?: string;
  email?: string;
  role?: string;
  is_eligible?: boolean;
}

interface MapStats {
  total_cohorts: number;
  total_farmers: number;
  total_farm_plots?: number;
  total_warehouses: number;
  total_aggregation_centers: number;
  total_area_hectares: number;
}

// ─── Utility ────────────────────────────────────────────────────────

const firstArray = <T,>(value: any, keys: string[]): T[] => {
  if (Array.isArray(value)) return value;
  for (const key of keys) {
    if (Array.isArray(value?.[key])) return value[key];
  }
  return [];
};

// ─── Main Component ─────────────────────────────────────────────────

const MapsPage: React.FC = () => {
  const { hasFeature, canAccessPage, user } = useAuth();
  const canManageFieldTeam = ['super_admin', 'project_manager', 'tenant_admin', 'business_manager', 'field_manager']
    .includes(String(user?.role || '').toLowerCase());
  // Optional layers follow their own module entitlement. Field boundaries stay
  // available on their own when only Maps is enabled.
  const layerAccess = useMemo(() => ({
    farmers: hasFeature('farmers') && canAccessPage('farmers'),
    trees: hasFeature('traceability') && canAccessPage('traceability'),
    cohorts: hasFeature('cohorts') && canAccessPage('cohorts'),
    warehouses: hasFeature('warehouse') && canAccessPage('warehouse'),
  }), [hasFeature, canAccessPage]);
  // ── Data (fetched from API) ──────────────────────────────
  const [farmerOptions, setFarmerOptions] = useState<FarmerOption[]>([]);
  const [staffOptions, setStaffOptions] = useState<StaffOption[]>([]);
  const [cropOptions, setCropOptions] = useState<string[]>([]);
  const [stats, setStats] = useState<MapStats | null>(null);
  const [extraLoading, setExtraLoading] = useState(true);

  // ── Layer management via hook ─────────────────────────────
  const {
    fields, trees, farmers, cohorts, warehouses,
    visibility, setVisibility, toggleLayer,
    loading, error, stats: layerStats, refresh, allCoords,
  } = useMapLayers({ include: layerAccess });

  // ── Viewport ──────────────────────────────────────────────
  const {
    viewport, navigateRequested, handleViewportChange,
    requestNavigate, zoomToRwanda, fitBounds,
  } = useMapViewport({
    initialCenter: [RWANDA_DEFAULTS.center.lng, RWANDA_DEFAULTS.center.lat] as GeoCoord,
    initialZoom: RWANDA_DEFAULTS.zoom,
  });

  // ── Selection ─────────────────────────────────────────────
  const {
    selection, selectField, selectTree, selectFarmer,
    selectCohort, selectWarehouse, clearSelection,
    focusCoordinate, focusZoom,
  } = useMapSelection();

  // ── Field creation state ──────────────────────────────────
  const [showCreateFieldModal, setShowCreateFieldModal] = useState(false);
  const [repairField, setRepairField] = useState<MapField | null>(null);
  const [drawMode, setDrawMode] = useState<'idle' | 'drawing' | 'editing'>('idle');
  const [draftBoundary, setDraftBoundary] = useState<GeoCoord[]>([]);
  const [selectedFieldFarmerIds, setSelectedFieldFarmerIds] = useState<number[]>([]);
  const [drawingWorkspace, setDrawingWorkspace] = useState(false);
  const [creatingField, setCreatingField] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // ── Field detail state ────────────────────────────────────
  const [fieldProfileTab, setFieldProfileTab] = useState<'overview' | 'farmers' | 'staff' | 'activities' | 'diary'>('overview');
  const [fieldFarmers, setFieldFarmers] = useState<FieldFarmer[]>([]);
  const [fieldFarmersLoading, setFieldFarmersLoading] = useState(false);
  const [fieldDiary, setFieldDiary] = useState<DiaryEntrySummary[]>([]);
  const [fieldDiaryLoading, setFieldDiaryLoading] = useState(false);
  const [fieldHistory, setFieldHistory] = useState<FieldHistoryEntry[]>([]);
  const [fieldHistoryLoading, setFieldHistoryLoading] = useState(false);
  const [fieldHistoryError, setFieldHistoryError] = useState('');
  const [showFieldTeamDialog, setShowFieldTeamDialog] = useState(false);
  const [teamField, setTeamField] = useState<MapField | null>(null);
  const [teamAgronomistOptions, setTeamAgronomistOptions] = useState<StaffOption[]>([]);
  const [teamAgronomistIds, setTeamAgronomistIds] = useState<number[]>([]);
  const [teamPrimaryAgronomistId, setTeamPrimaryAgronomistId] = useState<number | null>(null);
  const [teamAgronomistSearch, setTeamAgronomistSearch] = useState('');
  const [teamFarmerIds, setTeamFarmerIds] = useState<number[]>([]);
  const [teamPrimaryFarmerId, setTeamPrimaryFarmerId] = useState<number | null>(null);
  const [teamFarmerSearch, setTeamFarmerSearch] = useState('');
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamLoaded, setTeamLoaded] = useState(false);
  const [teamSaving, setTeamSaving] = useState(false);
  const [teamError, setTeamError] = useState('');
  const [adminBoundaryLevel, setAdminBoundaryLevel] = useState<BoundaryLevel>('districts');
  const hasFitInitialDataRef = useRef(false);

  const sketchMode = drawMode === 'drawing';
  const isCompact = typeof window !== 'undefined' && window.innerWidth < 768;
  const hasIneligibleSelectedAgronomist = teamAgronomistIds.some((id) =>
    teamAgronomistOptions.some((staff) => staff.id === id && staff.is_eligible === false));

  // Frame persisted operational geography once after the initial layer load.
  // Explicit URL navigation and drawing sessions retain control of the camera.
  useEffect(() => {
    if (loading || hasFitInitialDataRef.current || drawingWorkspace) return;
    const params = new URLSearchParams(window.location.search);
    if (params.has('field') || params.get('create') === '1') return;

    const operationalCoords: GeoCoord[] = [
      ...fields.flatMap((field) => field.boundary),
      ...fields.flatMap((field) => field.boundary.length ? [] : (field.center ? [field.center] : [])),
      ...trees.map((tree) => tree.position),
    ];
    if (!operationalCoords.length) return;

    hasFitInitialDataRef.current = true;
    fitBounds(operationalCoords);
  }, [drawingWorkspace, fields, fitBounds, loading, trees]);

  // ── Fetch extra data (farmer/staff options, crops, stats) ─
  useEffect(() => {
    const loadExtra = async () => {
      try {
        const [farmerListData, staffData, cropsData, statsData] = await Promise.all([
          apiGet('/api/farmers').catch(() => []),
          apiGet('/api/users').catch(() => []),
          apiGet<any>('/api/crops', { page: 1, limit: 200 }, { silent: true }).catch(() => ({ crops: [] })),
          apiGet('/api/maps/stats').catch(() => null),
        ]);
        setFarmerOptions(firstArray<FarmerOption>(farmerListData, ['farmers', 'data', 'rows']));
        setStaffOptions(firstArray<StaffOption>(staffData, ['users', 'data', 'rows']));
        const cropRows = Array.isArray((cropsData as any)?.crops) ? (cropsData as any).crops : [];
        const fallbackCrops = ['Avocado', 'Macadamia', 'Coffee', 'Tea', 'Banana', 'Bean'];
        setCropOptions(
          buildCropOptions([
            ...cropRows.map((crop: any) => crop.crop_type || crop.product_type || crop.name),
            ...fields.map((f) => f.cropType),
            ...trees.map((t) => t.cropType),
            ...fallbackCrops,
          ]).map((option) => option.label),
        );
        if (statsData) setStats(statsData as MapStats);
      } catch (err) {
        console.error('Error loading extra data:', err);
      } finally {
        setExtraLoading(false);
      }
    };
    loadExtra();
  }, []);

  // ── URL params: ?field=<id> or ?create=1&farmer=<id> ──────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fieldId = Number(params.get('field') || 0);
    if (fieldId && fields.length && !showCreateFieldModal) {
      const field = fields.find((f) => f.id === fieldId);
      if (field) {
        handleFieldClick(field);
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }
    }
    const createParam = params.get('create');
    const farmerParam = Number(params.get('farmer') || 0);
    if (createParam === '1' && farmerParam && farmerOptions.length > 0 && !showCreateFieldModal) {
      openCreateFarmField();
      setSelectedFieldFarmerIds([farmerParam]);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [fields, showCreateFieldModal, farmerOptions]);

  // ── Selection effect: navigate + load detail data ─────────
  useEffect(() => {
    if (focusCoordinate) {
      requestNavigate(focusCoordinate, focusZoom);
    }
  }, [focusCoordinate, focusZoom, requestNavigate]);

  useEffect(() => {
    if (selection.type === 'field' && selection.entity) {
      const field = selection.entity as MapField;
      setFieldProfileTab('overview');
      loadFieldFarmers(field);
    }
  }, [selection.type, selection.entity?.id]);

  useEffect(() => {
    if (selection.type !== 'field' || !selection.entity) return;
    const field = selection.entity as MapField;
    if (fieldProfileTab === 'diary') {
      loadFieldDiary(field);
    } else if (fieldProfileTab === 'activities') {
      loadFieldHistory(field);
    }
  }, [selection.type, selection.entity?.id, fieldProfileTab]);

  const loadFieldFarmers = async (field: MapField) => {
    setFieldFarmersLoading(true);
    try {
      const data = await apiGet<any>(`/api/maps/farm-plots/${field.id}/farmers`);
      setFieldFarmers(Array.isArray(data?.farmers) ? data.farmers : []);
    } catch {
      setFieldFarmers(field.farmers || []);
    } finally {
      setFieldFarmersLoading(false);
    }
  };

  const openFieldTeamDialog = async (field: MapField) => {
    const assignedFarmers = fieldFarmers.length ? fieldFarmers : (field.farmers || []);
    const assignedIds = assignedFarmers
      .map((farmer) => Number(farmer.id))
      .filter((id) => Number.isInteger(id) && id > 0);
    const primary = assignedFarmers.find((farmer) =>
      ('is_primary' in farmer && farmer.is_primary) || ('isPrimary' in farmer && farmer.isPrimary));

    setTeamField(field);
    setTeamAgronomistIds([]);
    setTeamPrimaryAgronomistId(null);
    setTeamAgronomistOptions([]);
    setTeamAgronomistSearch('');
    setTeamFarmerIds(assignedIds);
    setTeamPrimaryFarmerId(Number(primary?.id || field.farmerId || assignedIds[0]) || null);
    setTeamFarmerSearch('');
    setTeamError('');
    setTeamLoaded(false);
    setShowFieldTeamDialog(true);
    setTeamLoading(true);
    try {
      const data = await apiGet<any>(`/api/maps/farm-plots/${field.id}/agronomists`);
      const available = firstArray<StaffOption>(data, ['available_agronomists']);
      const assigned = firstArray<any>(data, ['agronomists']);
      const ids = assigned.map((person) => Number(person.id)).filter((id) => Number.isInteger(id) && id > 0);
      const primary = assigned.find((person) => person.is_primary === true);
      setTeamAgronomistOptions(available);
      setTeamAgronomistIds(ids);
      setTeamPrimaryAgronomistId(Number(primary?.id) || ids[0] || null);
      setTeamLoaded(true);
    } catch (error: any) {
      setTeamError(error?.message || 'Could not load this field’s agronomist team.');
      setTeamLoaded(false);
    } finally {
      setTeamLoading(false);
    }
  };

  const handleSaveFieldTeam = async () => {
    if (!teamField) return;
    if (teamFarmerIds.length === 0) {
      setTeamError('Keep at least one farmer assigned to this field.');
      return;
    }

    const primaryFarmerId = teamFarmerIds.includes(teamPrimaryFarmerId || 0)
      ? teamPrimaryFarmerId
      : teamFarmerIds[0];
    const primaryAgronomistId = teamAgronomistIds.includes(teamPrimaryAgronomistId || 0)
      ? teamPrimaryAgronomistId
      : teamAgronomistIds[0] || null;
    setTeamSaving(true);
    setTeamError('');
    try {
      await apiPatch(`/api/maps/farm-plots/${teamField.id}`, {
        agronomist_ids: teamAgronomistIds,
        primary_agronomist_id: primaryAgronomistId,
        farmer_ids: teamFarmerIds,
        primary_farmer_id: primaryFarmerId,
      });

      const primaryFarmer = farmerOptions.find((farmer) => farmer.id === primaryFarmerId);
      const nextFarmers: FieldFarmer[] = teamFarmerIds.map((id) => {
        const option = farmerOptions.find((farmer) => farmer.id === id);
        const current = fieldFarmers.find((farmer) => farmer.id === id);
        return {
          id,
          name: option?.full_name || option?.name || current?.name,
          phone: option?.phone || current?.phone,
          farmer_code: option?.farmer_code || current?.farmer_code,
          is_primary: id === primaryFarmerId,
        };
      });
      const agronomist = teamAgronomistOptions.find((staff) => staff.id === primaryAgronomistId);
      setFieldFarmers(nextFarmers);
      selectField({
        ...teamField,
        assignedAgronomistId: primaryAgronomistId,
        assignedAgronomistName: primaryAgronomistId ? (agronomist?.full_name || agronomist?.email || null) : null,
        agronomists: teamAgronomistIds.map((id) => {
          const person = teamAgronomistOptions.find((staff) => staff.id === id);
          return {
            id,
            name: person?.full_name,
            email: person?.email,
            isPrimary: id === primaryAgronomistId,
          };
        }),
        agronomistCount: teamAgronomistIds.length,
        farmerId: primaryFarmerId || undefined,
        farmerName: primaryFarmer?.full_name || primaryFarmer?.name || teamField.farmerName,
        primaryFarmerName: primaryFarmer?.full_name || primaryFarmer?.name || teamField.primaryFarmerName,
        farmerCount: teamFarmerIds.length,
        farmers: nextFarmers,
      });
      setShowFieldTeamDialog(false);
      setTeamField(null);
      setSuccessMessage(`Team assignments updated for ${teamField.name}.`);
      setTimeout(() => setSuccessMessage(''), 5000);
      await refresh();
    } catch (error: any) {
      setTeamError(error?.message || 'Could not update this field team. Please try again.');
    } finally {
      setTeamSaving(false);
    }
  };

  useEffect(() => {
    if (!showFieldTeamDialog) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !teamSaving) {
        setShowFieldTeamDialog(false);
        setTeamField(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showFieldTeamDialog, teamSaving]);

  const loadFieldDiary = async (field: MapField) => {
    setFieldDiaryLoading(true);
    try {
      const data = await apiGet<any>(`/api/field-diary?field_plot_id=${field.id}`);
      const list = data?.entries ?? data?.data ?? [];
      setFieldDiary(Array.isArray(list) ? list.slice(0, 50) : []);
    } catch {
      setFieldDiary([]);
    } finally {
      setFieldDiaryLoading(false);
    }
  };

  const loadFieldHistory = async (field: MapField) => {
    setFieldHistoryLoading(true);
    setFieldHistoryError('');
    try {
      const data = await apiGet<any>(`/api/activities/fields/${field.id}/history`);
      setFieldHistory(data?.history ?? data?.data ?? []);
    } catch {
      setFieldHistory([]);
      setFieldHistoryError('Could not load field history.');
    } finally {
      setFieldHistoryLoading(false);
    }
  };

  // ── Drawing ───────────────────────────────────────────────
  const handleSketchPoint = useCallback((point: GeoCoord) => {
    if (!sketchMode) return;
    setDraftBoundary((prev) => [...prev, point]);
  }, [sketchMode]);

  const computeSketchAreaHa = (points: GeoCoord[]): number => {
    return calculateAreaHectares(points);
  };

  const sketchCenter = useMemo(
    () => draftBoundary.length > 0
      ? [
          draftBoundary.reduce((sum, p) => sum + p[0], 0) / draftBoundary.length,
          draftBoundary.reduce((sum, p) => sum + p[1], 0) / draftBoundary.length,
        ] as GeoCoord
      : RWANDA_DEFAULTS.center as unknown as GeoCoord,
    [draftBoundary],
  );

  // ── Field creation ────────────────────────────────────────
  const openCreateFarmField = () => {
    clearSelection();
    setRepairField(null);
    setShowCreateFieldModal(true);
    setDrawingWorkspace(false);
    setDrawMode('idle');
    setDraftBoundary([]);
    setSelectedFieldFarmerIds([]);
  };

  const closeCreateFarmField = () => {
    setShowCreateFieldModal(false);
    setRepairField(null);
    setDrawingWorkspace(false);
    setDrawMode('idle');
    setDraftBoundary([]);
    setSelectedFieldFarmerIds([]);
  };

  const handleFieldClick = (field: MapField) => {
    selectField(field);
  };

  const handleTreeClick = (tree: MapTree) => {
    selectTree(tree);
  };

  const handleFarmerClick = (farmer: MapFarmer) => {
    selectFarmer(farmer);
  };

  const handleCohortClick = (cohort: MapCohort) => {
    selectCohort(cohort);
  };

  const handleWarehouseClick = (warehouse: MapWarehouse) => {
    selectWarehouse(warehouse);
  };

  const handleDeleteField = async (field: MapField) => {
    if (!window.confirm(`Delete "${field.name}"? This will also remove all trees and data on this field.`)) return;
    try {
      await apiDelete(`/api/maps/farm-plots/${field.id}`);
      clearSelection();
      setSuccessMessage(`Field "${field.name}" deleted.`);
      setTimeout(() => setSuccessMessage(''), 5000);
      await refresh();
    } catch (err: any) {
      alert(err?.message || 'Failed to delete field.');
    }
  };

  const handleCreateFarmField = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (creatingField) return;
    const form = new FormData(event.currentTarget);
    const boundary = draftBoundary.length >= 3 ? draftBoundary : [];
    const area = draftBoundary.length >= 3 ? computeSketchAreaHa(boundary) : Number(form.get('area_hectares') || 0.25);
    const farmerIds = selectedFieldFarmerIds.filter(Boolean);
    const centerLng = boundary.length > 0 ? boundary.reduce((s, p) => s + p[0], 0) / boundary.length : 30.0619;
    const centerLat = boundary.length > 0 ? boundary.reduce((s, p) => s + p[1], 0) / boundary.length : -1.9441;

    if (!form.get('name') || farmerIds.length === 0) {
      alert('Field name and at least one farmer are required.');
      return;
    }
    if (draftBoundary.length < 3) {
      alert('Sketch the farm boundary first by clicking at least 3 points on the map.');
      return;
    }

    try {
      setCreatingField(true);
      const payload = {
        name: String(form.get('name') || '').trim(),
        crop_type: normalizeCropName(String(form.get('crop_type') || 'Avocado')),
        area_hectares: area,
        expected_tree_count: 0,
        assigned_agronomist_id: form.get('assigned_agronomist_id') || null,
        boundary_coordinates: boundary,
        center_lat: centerLat,
        center_lng: centerLng,
        farmer_ids: farmerIds,
        primary_farmer_id: farmerIds[0],
      };

      const data = repairField
        ? await apiPatch<any>(`/api/maps/farm-plots/${repairField.id}`, payload)
        : await apiPost<any>('/api/maps/farm-plots', payload);

      setSuccessMessage(
        repairField
          ? `Field "${data?.plot?.name || payload.name}" boundary updated.`
          : `Field "${data?.plot?.name || payload.name}" created with ${farmerIds.length} farmer(s).`,
      );
      setTimeout(() => setSuccessMessage(''), 5000);
      closeCreateFarmField();
      await refresh();

      // Zoom to the newly created/updated field
      const plot = data?.plot;
      if (plot) {
        const coords = toBoundary(plot.boundary_coordinates);
        if (coords.length >= 3) {
          fitBounds(coords);
        } else if (plot.center_lng != null && plot.center_lat != null) {
          requestNavigate([plot.center_lng, plot.center_lat] as GeoCoord, 16);
        }
      }
    } catch (err: any) {
      alert(err?.message || 'Failed to save field.');
    } finally {
      setCreatingField(false);
    }
  };

  // ── Drawing mode handlers ─────────────────────────────────
  const enterDrawingMode = () => {
    setDrawingWorkspace(true);
    setDrawMode('drawing');
  };

  const exitDrawingMode = () => {
    setDrawingWorkspace(false);
    setDrawMode(draftBoundary.length >= 3 ? 'editing' : 'idle');
  };

  // ── Search handler ────────────────────────────────────────
  const handleSearchSelect = (result: any) => {
    if (result.center) {
      requestNavigate(result.center, result.entityType === 'tree' ? 19 : 15);
    }
    // Navigate to entity if possible
    if (result.entityType === 'field' && result.entityId) {
      const field = fields.find((f) => f.id === result.entityId);
      if (field) selectField(field);
    } else if (result.entityType === 'tree' && result.entityId) {
      const tree = trees.find((t) => t.id === result.entityId);
      if (tree) selectTree(tree);
    }
  };

  // ── Fit bounds ────────────────────────────────────────────
  useEffect(() => {
    const w = window as any;
    w.__mapFitBounds = (coords: GeoCoord[]) => fitBounds(coords);
    return () => { delete w.__mapFitBounds; };
  }, [fitBounds]);

  // ── Selection detail drawer content ───────────────────────
  const renderSelectionDrawer = () => {
    if (!selection.entity) return null;

    if (selection.type === 'field') {
      const field = selection.entity as MapField;
      return (
        <div className="h-full bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="border-b border-slate-100 px-4 py-3 shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-slate-900">{field.name}</h3>
                <p className="text-[11px] text-slate-400">{field.plotCode} · {field.cropType || 'N/A'}</p>
              </div>
              <div className="flex items-center gap-1">
                {canManageFieldTeam && (
                  <button
                    onClick={() => openFieldTeamDialog(field)}
                    disabled={fieldFarmersLoading}
                    className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:cursor-wait disabled:opacity-50"
                    title="Assign agronomists and farmers to this field"
                    aria-label={`Manage agronomist and farmers for ${field.name}`}
                  >
                    <UserGroupIcon className="h-4 w-4" />
                    <span>{fieldFarmersLoading ? 'Loading' : 'Manage team'}</span>
                  </button>
                )}
                <button onClick={() => handleDeleteField(field)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors" title="Delete field">
                  <TrashIcon className="w-4 h-4" />
                </button>
                <button onClick={clearSelection} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
            {/* Tabs */}
            <div className="flex gap-1 mt-3 overflow-x-auto">
              {(['overview', 'farmers', 'activities', 'diary'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setFieldProfileTab(tab)}
                  className={`px-3 py-1.5 text-[11px] font-bold rounded-lg whitespace-nowrap transition-colors ${
                    fieldProfileTab === tab ? 'bg-emerald-600 text-white' : 'text-slate-500 hover:bg-slate-100'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {fieldProfileTab === 'overview' && (
              <div className="space-y-3">
                <InfoRow label="Area" value={`${Number(field.areaHectares || 0).toFixed(2)} ha`} />
                <InfoRow label="Status" value={field.status || 'Active'} />
                <InfoRow label="Farmers" value={`${field.farmerCount}`} />
                <InfoRow label="Trees" value={`${field.treeCount} (${field.mappedTreeCount} mapped)`} />
                <InfoRow label="Activities" value={`${field.activityCount}`} />
                <InfoRow label="Cohort" value={field.cohortName || '—'} />
                <InfoRow label="Agronomist" value={field.assignedAgronomistName || '—'} />
                <InfoRow label="Facilitator" value={field.assignedFacilitatorName || '—'} />
              </div>
            )}
            {fieldProfileTab === 'farmers' && (
              <div>
                {fieldFarmersLoading ? (
                  <div className="flex justify-center py-8"><div className="h-6 w-6 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" /></div>
                ) : fieldFarmers.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-8">No farmers assigned</p>
                ) : (
                  <div className="space-y-2">
                    {fieldFarmers.map((f) => (
                      <div key={f.id} className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-xs font-bold text-emerald-700">
                          {(f.name || '').slice(0, 2).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-slate-800 truncate">{f.name}</p>
                          <p className="text-[11px] text-slate-400">{f.farmer_code || ''} {f.phone || ''}</p>
                        </div>
                        {f.is_primary && <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Primary</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {fieldProfileTab === 'activities' && (
              <div>
                {fieldHistoryLoading ? (
                  <div className="flex justify-center py-8"><div className="h-6 w-6 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" /></div>
                ) : fieldHistoryError ? (
                  <p className="text-xs text-red-500 text-center py-4">{fieldHistoryError}</p>
                ) : fieldHistory.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-8">No activities yet</p>
                ) : (
                  <div className="space-y-2">
                    {fieldHistory.map((h) => (
                      <div key={h.id} className="rounded-xl border border-slate-200 p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-slate-800">{h.assignment_code}</span>
                          <span className="text-[10px] text-slate-400">{h.submitted_at ? new Date(h.submitted_at).toLocaleDateString() : ''}</span>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-1">{h.activity_type_name} · {h.status}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {fieldProfileTab === 'diary' && (
              <div>
                {fieldDiaryLoading ? (
                  <div className="flex justify-center py-8"><div className="h-6 w-6 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" /></div>
                ) : fieldDiary.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-8">No diary entries</p>
                ) : (
                  <div className="space-y-2">
                    {fieldDiary.map((d) => (
                      <div key={d.id} className="rounded-xl border border-slate-200 p-3">
                        <p className="text-xs font-bold text-slate-800">{d.visit_date || 'No date'} · {d.visit_type || ''}</p>
                        <p className="text-[11px] text-slate-500 mt-1">{d.observations || '—'}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    // Tree detail
    if (selection.type === 'tree') {
      const tree = selection.entity as MapTree;
      return (
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-bold text-slate-900">{tree.treeCode}</h3>
              <p className="text-[11px] text-slate-400">{tree.cropType} · {tree.farmerName || 'N/A'}</p>
            </div>
            <button onClick={clearSelection} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <InfoRow label="Health" value={tree.healthStatus} />
            <InfoRow label="Status" value={tree.treeStatus} />
            <InfoRow label="Issues" value={`${tree.openIssueCount}`} />
            <InfoRow label="Cohort" value={tree.cohortName || '—'} />
          </div>
        </div>
      );
    }

    // Farmer/warehouse/cohort — simple card
    const entity = selection.entity as any;
    return (
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-bold text-slate-900">{entity.name}</h3>
            <p className="text-[11px] text-slate-400 capitalize">{selection.type}</p>
          </div>
          <button onClick={clearSelection} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  // ── Loading state ─────────────────────────────────────────
  if (loading && extraLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-600 mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Loading Farm Maps...</p>
        </div>
      </div>
    );
  }

  // ── Drawing tools overlay (rendered on top of map) ─────────
  const drawingTools = drawingWorkspace ? (
    <div className="w-80 max-w-full bg-white/95 backdrop-blur rounded-lg shadow-xl border border-slate-200 p-3">
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-2.5 h-2.5 rounded-full ${drawMode === 'drawing' ? 'bg-emerald-500 animate-pulse' : draftBoundary.length >= 3 ? 'bg-emerald-500' : 'bg-amber-400'}`} />
        <span className="text-sm font-bold text-slate-900">
          {drawMode === 'drawing' ? 'Drawing — click map' : draftBoundary.length >= 3 ? 'Boundary ready' : 'Click Start to begin'}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 text-center text-xs font-bold mb-3">
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2">
          <span className="text-xl font-black text-emerald-700 block">{draftBoundary.length}</span>
          <span className="text-[10px] uppercase tracking-wider text-emerald-600">Points</span>
        </div>
        <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2">
          <span className="text-xl font-black text-slate-800 block">{computeSketchAreaHa(draftBoundary).toFixed(2)}</span>
          <span className="text-[10px] uppercase tracking-wider text-slate-500">Hectares</span>
        </div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => setDrawMode(drawMode === 'drawing' ? 'editing' : 'drawing')}
          className={`flex-1 rounded-xl py-2.5 text-sm font-bold transition-all ${
            drawMode === 'drawing' ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-emerald-600 text-white hover:bg-emerald-700'
          }`}
        >
          {drawMode === 'drawing' ? 'Stop Drawing' : 'Start Drawing'}
        </button>
      </div>
      <div className="flex gap-2 mt-2">
        <button onClick={() => setDraftBoundary((p) => p.slice(0, -1))} disabled={draftBoundary.length === 0}
          className="flex-1 rounded-xl border border-slate-200 bg-white py-2 text-xs font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-40">↩ Undo</button>
        <button onClick={() => setDraftBoundary([])} disabled={draftBoundary.length === 0}
          className="flex-1 rounded-xl border border-slate-200 bg-white py-2 text-xs font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-40">✕ Clear</button>
      </div>
      <div className="mt-3 flex gap-2">
        <button onClick={exitDrawingMode} disabled={draftBoundary.length < 3}
          className="flex-1 rounded-xl bg-emerald-600 py-2.5 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-40">Done Drawing</button>
        <button onClick={() => { setDrawingWorkspace(false); setDrawMode('idle'); setDraftBoundary([]); closeCreateFarmField(); }}
          className="rounded-xl border border-slate-200 px-4 py-2.5 text-xs font-bold text-slate-500 hover:bg-slate-50">Cancel</button>
      </div>
    </div>
  ) : undefined;

  // ── Main render ───────────────────────────────────────────
  return (
    <div className="h-screen flex flex-col bg-slate-50 overflow-hidden font-sans">
      {/* Header */}
      <header className="h-14 lg:h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6 shadow-sm z-30 shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center shadow-lg shadow-emerald-200/20">
            <MapIcon className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-base lg:text-lg font-bold text-slate-900">Farm Maps</h1>
            <p className="text-[11px] font-medium text-emerald-600 uppercase tracking-wider hidden sm:block">Territory Management</p>
          </div>
        </div>
        <div className="hidden lg:flex items-center gap-2">
          {[
            { label: 'Fields', value: stats?.total_farm_plots || layerStats.fieldCount, color: 'text-emerald-500' },
            { label: 'Hectares', value: Number(stats?.total_area_hectares || 0).toFixed(1), color: 'text-blue-500' },
            { label: 'Facilities', value: stats?.total_warehouses || layerStats.warehouseCount, color: 'text-purple-500' },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-lg border border-slate-100">
              <div>
                <p className="text-[10px] text-slate-400 font-medium uppercase">{s.label}</p>
                <p className={`text-sm font-bold ${s.color} leading-tight`}>{s.value}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={openCreateFarmField}
            className="flex items-center gap-1.5 px-3 lg:px-4 py-2 bg-emerald-600 text-white text-xs lg:text-sm font-semibold rounded-xl hover:bg-emerald-700 shadow-lg shadow-emerald-200 transition-all">
            <PlusIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Create Field</span>
          </button>
        </div>
      </header>

      {/* Success toast */}
      {successMessage && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-bold shadow-xl">
          ✓ {successMessage}
        </div>
      )}

      {/* Map workspace */}
      <div className="flex-1 relative">
        <MapWorkspace
          viewport={viewport}
          navigateRequested={navigateRequested}
          onViewportChange={handleViewportChange}
          fields={fields}
          trees={trees}
          farmers={farmers}
          cohorts={cohorts}
          warehouses={warehouses}
          visibility={visibility}
          onToggleLayer={toggleLayer}
          stats={layerStats}
          selectedFieldId={selection.type === 'field' ? selection.entity?.id : null}
          selectedTreeId={selection.type === 'tree' ? selection.entity?.id : null}
          onFieldClick={handleFieldClick}
          onTreeClick={handleTreeClick}
          onFarmerClick={handleFarmerClick}
          onCohortClick={handleCohortClick}
          onWarehouseClick={handleWarehouseClick}
          onSearchSelect={handleSearchSelect}
          loading={loading}
          error={error}
          adminBoundaryLevel={adminBoundaryLevel}
          onAdminBoundaryChange={setAdminBoundaryLevel}
          drawingMode={drawMode === 'drawing'}
          draftBoundary={draftBoundary}
          onDraftBoundaryChange={setDraftBoundary}
          drawingTools={drawingTools}
          selectionDrawer={selection.entity ? (
            <div className="h-full overflow-hidden">{renderSelectionDrawer()}</div>
          ) : undefined}
        />

        {/* Empty state — hidden while user is creating/drawing */}
        {!loading && !drawingWorkspace && !showCreateFieldModal && fields.length === 0 && trees.length === 0 && farmers.length === 0 && (
          <MapEmptyState
            title="No farm data yet"
            description="Create a farm field with a boundary to see it on the map."
            action={{ label: 'Create Field', onClick: openCreateFarmField }}
          />
        )}

        {/* Error state */}
        {error && !loading && fields.length === 0 && trees.length === 0 && farmers.length === 0 && cohorts.length === 0 && warehouses.length === 0 && (
          <MapErrorState error={error} onRetry={refresh} />
        )}
      </div>

      {showFieldTeamDialog && teamField && (
        <div
          className="fixed inset-0 z-[950] flex items-center justify-center bg-slate-950/45 p-3 sm:p-6"
          onClick={() => {
            if (!teamSaving) {
              setShowFieldTeamDialog(false);
              setTeamField(null);
            }
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="field-team-title"
            className="flex max-h-[92vh] w-full max-w-xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <h2 id="field-team-title" className="text-lg font-bold text-slate-900">Manage field team</h2>
                <p className="mt-1 text-sm text-slate-500">{teamField.name} · {teamField.plotCode || 'Mapped field'}</p>
              </div>
              <button
                type="button"
                onClick={() => { setShowFieldTeamDialog(false); setTeamField(null); }}
                disabled={teamSaving}
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-50"
                aria-label="Close field team dialog"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </header>

            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
              <p className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                This sets the standing agronomist team for this field; it does not create or send a farmer task. To schedule work, use Activities and choose the farmer recipients. The saved boundary will not change.
              </p>

              <section aria-labelledby="field-agronomists-title">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 id="field-agronomists-title" className="text-sm font-semibold text-slate-800">Agronomists</h3>
                  <p className="mt-0.5 text-xs text-slate-500">{teamAgronomistIds.length} assigned · no two-person limit</p>
                  </div>
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">{teamAgronomistOptions.length} available</span>
                </div>
                <label className="mt-3 block">
                  <span className="sr-only">Search workspace agronomists</span>
                  <input
                    value={teamAgronomistSearch}
                    onChange={(event) => setTeamAgronomistSearch(event.target.value)}
                    placeholder="Search agronomists by name or email"
                    disabled={teamSaving || teamLoading}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </label>
                <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-slate-200">
                  {teamLoading ? (
                    <p className="p-4 text-sm text-slate-500">Loading agronomists…</p>
                  ) : teamAgronomistOptions
                    .filter((staff) => [staff.full_name, staff.email]
                      .some((value) => String(value || '').toLowerCase().includes(teamAgronomistSearch.trim().toLowerCase())))
                    .map((staff) => {
                      const checked = teamAgronomistIds.includes(staff.id);
                      const name = staff.full_name || staff.email || `Agronomist ${staff.id}`;
                      return (
                        <div key={staff.id} className="flex items-center justify-between gap-3 border-b border-slate-100 px-3 py-2.5 last:border-b-0 hover:bg-slate-50">
                          <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={teamSaving}
                              onChange={(event) => setTeamAgronomistIds((current) => event.target.checked
                                ? [...current, staff.id]
                                : current.filter((id) => id !== staff.id))}
                              className="h-4 w-4 rounded border-slate-300 text-emerald-700 focus:ring-emerald-600"
                            />
                            <span className="min-w-0">
                              <span className="block truncate text-sm font-medium text-slate-900">{name}</span>
                              <span className={`block truncate text-xs ${staff.is_eligible === false ? 'text-amber-700' : 'text-slate-500'}`}>
                                {staff.is_eligible === false ? 'Workspace access inactive · remove to save' : staff.email || ''}
                              </span>
                            </span>
                          </label>
                          {checked && (
                            <button
                              type="button"
                              onClick={() => setTeamPrimaryAgronomistId(staff.id)}
                              disabled={teamSaving}
                              className={`shrink-0 rounded-md px-2 py-1 text-xs font-semibold ${teamPrimaryAgronomistId === staff.id ? 'bg-emerald-100 text-emerald-800' : 'text-slate-500 hover:bg-slate-100'}`}
                            >
                              {teamPrimaryAgronomistId === staff.id ? 'Primary' : 'Make primary'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  {!teamLoading && teamAgronomistOptions.length === 0 && (
                    <p className="p-4 text-sm text-slate-500">No active agronomists are available in this workspace.</p>
                  )}
                </div>
              </section>

              {hasIneligibleSelectedAgronomist && (
                <p role="alert" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Remove the inactive agronomist before saving. Only active agronomists in this workspace can be assigned.
                </p>
              )}

              <section aria-labelledby="field-farmers-title">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 id="field-farmers-title" className="text-sm font-semibold text-slate-800">Farmers on this field</h3>
                    <p className="mt-0.5 text-xs text-slate-500">{teamFarmerIds.length} selected · keep at least one assigned</p>
                  </div>
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">{farmerOptions.length} available</span>
                </div>
                <label className="mt-3 block">
                  <span className="sr-only">Search workspace farmers</span>
                  <input
                    value={teamFarmerSearch}
                    onChange={(event) => setTeamFarmerSearch(event.target.value)}
                    placeholder="Search farmers by name, phone, or code"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </label>
                <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-slate-200">
                  {farmerOptions
                    .filter((farmer) => [farmer.full_name, farmer.name, farmer.phone, farmer.farmer_code]
                      .some((value) => String(value || '').toLowerCase().includes(teamFarmerSearch.trim().toLowerCase())))
                    .map((farmer) => {
                      const checked = teamFarmerIds.includes(farmer.id);
                      const name = farmer.full_name || farmer.name || `Farmer ${farmer.id}`;
                      return (
                        <div key={farmer.id} className="flex items-center justify-between gap-3 border-b border-slate-100 px-3 py-2.5 last:border-b-0 hover:bg-slate-50">
                          <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={teamSaving}
                              onChange={(event) => setTeamFarmerIds((current) => event.target.checked
                                ? [...current, farmer.id]
                                : current.filter((id) => id !== farmer.id))}
                              className="h-4 w-4 rounded border-slate-300 text-emerald-700 focus:ring-emerald-600"
                            />
                            <span className="min-w-0">
                              <span className="block truncate text-sm font-medium text-slate-900">{name}</span>
                              <span className="block truncate text-xs text-slate-500">{[farmer.farmer_code, farmer.phone].filter(Boolean).join(' · ') || 'No contact details'}</span>
                            </span>
                          </label>
                          {checked && (
                            <button
                              type="button"
                              onClick={() => setTeamPrimaryFarmerId(farmer.id)}
                              disabled={teamSaving}
                              className={`shrink-0 rounded-md px-2 py-1 text-xs font-semibold ${teamPrimaryFarmerId === farmer.id ? 'bg-amber-100 text-amber-800' : 'text-slate-500 hover:bg-slate-100'}`}
                            >
                              {teamPrimaryFarmerId === farmer.id ? 'Primary' : 'Make primary'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  {farmerOptions.length === 0 && (
                    <p className="p-4 text-sm text-slate-500">No farmers are available in this workspace yet.</p>
                  )}
                </div>
              </section>

              {teamError && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{teamError}</p>}
            </div>

            <footer className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
              <button
                type="button"
                onClick={() => { setShowFieldTeamDialog(false); setTeamField(null); }}
                disabled={teamSaving}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >Cancel</button>
              <button
                type="button"
                onClick={handleSaveFieldTeam}
                disabled={teamSaving || teamLoading || !teamLoaded || farmerOptions.length === 0 || hasIneligibleSelectedAgronomist}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:cursor-wait disabled:opacity-60"
              >
                {teamSaving ? 'Saving…' : 'Save assignments'}
              </button>
            </footer>
          </section>
        </div>
      )}

      {/* Create field modal (bottom sheet on mobile, floating on desktop) */}
      {showCreateFieldModal && !drawingWorkspace && (
        <div className="fixed bottom-0 left-0 right-0 z-[900] flex max-h-[85vh] flex-col rounded-t-3xl bg-white shadow-2xl ring-1 ring-slate-200 lg:bottom-6 lg:left-auto lg:right-6 lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:w-[420px] lg:rounded-2xl">
          <div className="border-b border-slate-100 px-5 py-4 shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center">
                  <MapPinIcon className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-slate-900">{repairField ? 'Edit Field' : 'Create Farm Field'}</h2>
                  <p className="text-[11px] text-slate-400">Draw boundary then fill details</p>
                </div>
              </div>
              <button onClick={closeCreateFarmField} className="p-2 rounded-xl text-slate-400 hover:bg-slate-100">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          <form onSubmit={handleCreateFarmField} className="flex flex-col min-h-0 flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
              {/* Boundary section */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold flex items-center justify-center">1</span>
                  <h3 className="text-sm font-bold text-slate-900">Farm Boundary</h3>
                  {draftBoundary.length >= 3 && (
                    <span className="ml-auto text-[11px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                      ✓ {computeSketchAreaHa(draftBoundary).toFixed(2)} ha
                    </span>
                  )}
                </div>
                <button type="button" onClick={enterDrawingMode}
                  className={`w-full rounded-xl border-2 border-dashed p-4 text-center transition-all ${
                    draftBoundary.length >= 3 ? 'border-emerald-300 bg-emerald-50 hover:bg-emerald-100' : 'border-slate-300 bg-slate-50 hover:border-emerald-300'
                  }`}>
                  {draftBoundary.length >= 3 ? (
                    <><CheckCircleIcon className="w-5 h-5 text-emerald-600 mx-auto" /><p className="mt-1 text-sm font-bold text-emerald-800">Boundary drawn</p><p className="text-xs text-emerald-600">{draftBoundary.length} points · Click to redraw</p></>
                  ) : (
                    <><MapPinIcon className="w-8 h-8 text-slate-400 mx-auto" /><p className="mt-2 text-sm font-bold text-slate-700">Open map to draw boundary</p></>
                  )}
                </button>
              </div>

              {/* Field details */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center">2</span>
                  <h3 className="text-sm font-bold text-slate-900">Field Details</h3>
                </div>
                <div className="space-y-3">
                  <label className="block">
                    <span className="text-xs font-semibold text-slate-500">Field name *</span>
                    <input name="name" required placeholder="e.g. North Block A" defaultValue={repairField?.name || ''}
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                      <span className="text-xs font-semibold text-slate-500">Crop type *</span>
                      <input name="crop_type" required defaultValue={repairField?.cropType || 'avocado'} list="maps-crop-options"
                        className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
                      <datalist id="maps-crop-options">{cropOptions.map((c) => <option key={c} value={c} />)}</datalist>
                    </label>
                    <label className="block">
                      <span className="text-xs font-semibold text-slate-500">Area (ha)</span>
                      <input name="area_hectares" type="number" step="0.01" min="0.01" readOnly={draftBoundary.length >= 3}
                        defaultValue={draftBoundary.length >= 3 ? computeSketchAreaHa(draftBoundary) : (repairField?.areaHectares || 0.25)}
                        className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm bg-slate-50" />
                    </label>
                  </div>
                  <label className="block">
                    <span className="text-xs font-semibold text-slate-500">Agronomist</span>
                    <select name="assigned_agronomist_id" defaultValue={repairField?.assignedAgronomistId || ''}
                      className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-emerald-500">
                      <option value="">Not assigned</option>
                      {staffOptions.filter((s) => s.role === 'agronomist').map((s) => (
                        <option key={s.id} value={s.id}>{s.full_name || s.email}</option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>

              {/* Farmer assignment */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center">3</span>
                  <h3 className="text-sm font-bold text-slate-900">Assign Farmers</h3>
                  <span className="ml-auto text-[11px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                    {selectedFieldFarmerIds.length} selected
                  </span>
                </div>
                <div className="max-h-48 overflow-y-auto rounded-xl border border-slate-200 divide-y divide-slate-100">
                  {farmerOptions.map((farmer) => {
                    const checked = selectedFieldFarmerIds.includes(farmer.id);
                    return (
                      <button key={farmer.id} type="button"
                        onClick={() => setSelectedFieldFarmerIds((prev) => prev.includes(farmer.id) ? prev.filter((id) => id !== farmer.id) : [...prev, farmer.id])}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all ${checked ? 'bg-emerald-50' : 'hover:bg-slate-50'}`}>
                        <input type="checkbox" checked={checked} readOnly className="w-4 h-4 rounded border-slate-300 text-emerald-600" />
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-slate-800 truncate">{farmer.full_name || farmer.name || `Farmer ${farmer.id}`}</p>
                          <p className="text-[11px] text-slate-400">{[farmer.farmer_code, farmer.phone].filter(Boolean).join(' · ')}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-slate-100 px-5 py-3 shrink-0 flex gap-2">
              <button type="button" onClick={closeCreateFarmField}
                className="px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-bold text-slate-600 hover:bg-slate-50">Cancel</button>
              <button type="submit" disabled={creatingField || draftBoundary.length < 3}
                className="flex-1 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-40 shadow-lg shadow-emerald-200">
                {creatingField ? 'Saving...' : repairField ? 'Update Field' : 'Create Field'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

// ─── Small helpers ──────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="text-xs font-bold text-slate-800">{value}</span>
    </div>
  );
}

export default MapsPage;
